<template>
  <canvas ref="canvas" :width="width" :height="height" />
</template>

<script>
/* eslint-disable */

import { setState } from '@lib/systems/state'
import { enableUpdate, useUpdate } from '@lib/systems/update'
import { input, enableMouseInput, useMouseInput, RIGHT_BUTTON } from '@lib/systems/mouseInput'
import { enableWindowEvents, useResize } from '@lib/systems/windowEvents'
import { enablePixelRenderer, usePixelRenderer, renderer } from '@lib/renderers/pixel'
import { enableCamera, useCamera } from '@lib/systems/camera'
import { randomRange } from '@lib/util/random'
import debug from '@lib/util/debug'

import useCursor from '@game/systems/cursor'
import usePoms from '@game/systems/poms'
import useUi from '@game/systems/ui'
import editBoard from '@game/util/editBoard'
import setBoard from '@game/util/setBoard'
import drawApi from '@game/drawApi'
import { setTranslateOpts } from '@game/util/translatePomPos'
import { boards } from '@game/boards'
import { usePalette } from '@game/systems/palette'
import { enableWind, useWind } from '@game/systems/wind'
import { enableParticles, useParticles } from '@game/systems/particles'
import { parallax, enableParallax } from '@game/systems/parallax'
import { computer } from '@game/util/computerPlayer'

import {
  UPDATES_PER_SECOND,
  LOCAL,
  COMPUTER
} from '@game/constants'

// const isPowerOfTwo = (val) => Math.log2(val) % 1 === 0
const isEven = (val) => val % 2 === 0
const isWhole = (val) => val % 1 === 0

function closestPower (val) {
  val--
  val |= val >> 1
  val |= val >> 2
  val |= val >> 4
  val |= val >> 8
  val |= val >> 16
  return val + 1
}

function scaleToViewport (maxScale) {
  let foundW, foundH
  let width = window.innerWidth
  let height = window.innerHeight

  const scale = (width >= height)
    ? Math.round((width / 1920) * maxScale)
    : Math.round((height / 1920) * maxScale)

  width /= scale
  height /= scale

  while (!foundW) {
    if (isWhole(width / 2) && isEven(width / 2)) foundW = width
    width = closestPower(width)
  }

  while (!foundH) {
    if (isWhole(height / 2) && isEven(height / 2)) foundH = height
    height = closestPower(height)
  }

  return {
    width: foundW,
    height: foundH,
    scale
  }
}

export default {
  data () {
    return {
      width: 384,
      height: 216,
      scale: 3,
      maxScale: 5,
      particleInterval: null
    }
  },

  mounted () {
    const { canvas } = this.$refs

    // Debug tools
    debug.draw = false

    // Default state
    setState('arcade', {})

    // Update loop
    enableUpdate(UPDATES_PER_SECOND)

    // Mouse input
    enableMouseInput(canvas, {
      scale: this.scale,
      screenWidth: canvas.width,
      screenHeight: canvas.height
    })
    const {
      btnHeld,
      btnPressed,
      position: mousePos
    } = useMouseInput()

    // Camera
    enableCamera()
    const {
      cameraPosition: camera,
      setCamera
    } = useCamera(0, 0)

    // Main game update loop
    let camX, camY, camStartX, camStartY, mouseStartX, mouseStartY
    useUpdate(function () {
      if (computer.resolving) return

      // useMouseInput, RIGHT_BUTTON
      if (btnPressed(RIGHT_BUTTON)) {
        camX = 0
        camY = 0
        camStartX = camera.x
        camStartY = camera.y
        mouseStartX = mousePos.x
        mouseStartY = mousePos.y
      }
      if (btnHeld(RIGHT_BUTTON)) {
        camX = camStartX - (mousePos.x - mouseStartX)
        camY = camStartY - (mousePos.y - mouseStartY)
        setCamera({ x: camX, y: camY })
      }
    })

    // Pixel renderer
    const {
      currentPalette
    } = usePalette()

    enablePixelRenderer(canvas, {
      paletteSrc: 'palette.png',
      scale: this.scale,
      drawApis: drawApi,
      fragmentShader: `
        precision highp float;

        varying highp vec2 vTextureCoord;

        uniform sampler2D uScreenSampler;
        uniform sampler2D uPaletteSampler;

        void main () {
          float i = floor(texture2D(uScreenSampler, vTextureCoord).a*255.0);
          float x = mod(i, 16.0);
          float y = floor(i/16.0);

          vec4 color = texture2D(uPaletteSampler, vec2(x/15.0, y/15.0));

          gl_FragColor = color;
        }
      `
    })

    usePixelRenderer(function (ctx) {
      const pal = currentPalette()
      ctx.clear(pal.background)

      renderer.translate.x = Math.round(camera.x)
      renderer.translate.y = Math.round(camera.y)
      ctx.rectFillBlinds(-127, -127, canvas.width + 127, canvas.height + 127, pal.background - 1)
      ctx.circFill(canvas.width / 2, canvas.height / 2, 180, pal.background)
    }, {
      drawOrder: 0
    })

    // Parallax rendering
    enableParallax(canvas, 0.5, 0.95) // 0.95

    // Window events
    enableWindowEvents()
    useResize(() => {
      // const { width, height, scale } = scaleToViewport(this.maxScale)
      // input.resize(width, height, scale)
      // renderer.resize(width, height, scale)
      // this.scale = scale

      setTranslateOpts({
        viewport: {
          x: canvas.width,
          y: canvas.height
        }
      })
    })

    // Game systems
    usePoms()
    useCursor()
    // useUi()

    // Wind
    enableWind()
    const { direction: wind } = useWind()

    // Particles
    enableParticles()
    const { emit } = useParticles()
    clearInterval(this.particleInterval)
    this.particleInterval = setInterval(() => {
      const pal = currentPalette()
      emit(
        1,
        canvas.width * Math.random(), canvas.height * Math.random(),
        [pal.accent, pal.player1, pal.player2],
        {
          life: 500
        }
      )
    }, 10000)

    wind.x = randomRange(-0.2, 0.2)
    wind.y = randomRange(-0.2, 0.2)
    setBoard({
      board: boards[0],
      opponentMode: COMPUTER,
      opponentDifficulty: 1
    })
    // editBoard()
  }
}
</script>

<style scoped>
canvas {
  cursor: none;
  user-select: none;
}
</style>
